import {
  doc, setDoc, getDoc, collection,
  addDoc, updateDoc, deleteDoc,
  onSnapshot, serverTimestamp, query,
  where, getDocs, arrayUnion
} from "firebase/firestore";
import { db } from "./firebase";

// ── PROFILE ──────────────────────────────────────────
export const saveProfile = async (userId, data) => {
  await setDoc(doc(db, "users", userId), data, { merge: true });
};

export const getProfile = async (userId) => {
  const snap = await getDoc(doc(db, "users", userId));
  return snap.exists() ? snap.data() : null;
};

// ── GROUPS ───────────────────────────────────────────
export const createGroup = async (userId, groupData) => {
  const inviteCode = "HM-" + Math.random().toString(36).substr(2, 6).toUpperCase();
  const ref = await addDoc(collection(db, "groups"), {
    name: groupData.name,
    school: groupData.school || "",
    hall: groupData.hall || "",
    roomType: groupData.roomType || "",
    dormDimensions: groupData.dormDimensions || { length: 14, width: 12 },
    inviteCode,
    members: [userId],
    memberNames: { [userId]: groupData.creatorName || "You" },
    
    moveInDate: groupData.moveInDate || "",
    mode: "movein",
    createdBy: userId,
    createdAt: serverTimestamp(),
  });
  return { id: ref.id, inviteCode };
};

export const joinGroupByCode = async (userId, userName, code, venmo) => {
  const q = query(collection(db, "groups"), where("inviteCode", "==", code.toUpperCase()));
  const result = await getDocs(q);
  if (result.empty) return { error: "Invalid invite code" };
  const groupDoc = result.docs[0];
  await updateDoc(groupDoc.ref, {
    members: arrayUnion(userId),
    [`memberNames.${userId}`]: userName,
    
  });
  return { success: true, groupId: groupDoc.id };
};

// Read-only lookup for the invite preview screen
export const getGroupByCode = async (code) => {
  const q = query(collection(db, "groups"), where("inviteCode", "==", code.toUpperCase()));
  const result = await getDocs(q);
  if (result.empty) return null;
  const d = result.docs[0];
  return { id: d.id, ...d.data() };
};

export const getItemsOnce = async (groupId) => {
  const snap = await getDocs(collection(db, "groups", groupId, "items"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};
export const leaveGroup = async (groupId, userId) => {
  const groupRef = doc(db, "groups", groupId);
  const snap = await getDoc(groupRef);
  if (!snap.exists()) return;
  const data = snap.data();
  const newMembers = (data.members || []).filter(id => id !== userId);
  const newMemberNames = { ...(data.memberNames || {}) };
  delete newMemberNames[userId];
  await updateDoc(groupRef, {
    members: newMembers,
    memberNames: newMemberNames,
  });
};
export const deleteGroup = async (groupId) => {
  await deleteDoc(doc(db, "groups", groupId));
};

export const updateGroup = async (groupId, data) => {
  await updateDoc(doc(db, "groups", groupId), data);
};

export const updateGroupDimensions = async (groupId, dimensions) => {
  await updateDoc(doc(db, "groups", groupId), { dormDimensions: dimensions });
};

export const listenToGroups = (userId, callback) => {
  const q = query(collection(db, "groups"), where("members", "array-contains", userId));
  return onSnapshot(q, (snap) => {
    const groups = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(groups);
  });
};

// ── ITEMS ────────────────────────────────────────────
export const addItem = async (groupId, itemData) => {
  await addDoc(collection(db, "groups", groupId, "items"), {
    ...itemData,
    createdAt: serverTimestamp(),
  });
};

export const updateItem = async (groupId, itemId, data) => {
  await updateDoc(doc(db, "groups", groupId, "items", itemId), data);
};

export const deleteItem = async (groupId, itemId) => {
  await deleteDoc(doc(db, "groups", groupId, "items", itemId));
};

export const listenToItems = (groupId, callback) => {
  return onSnapshot(collection(db, "groups", groupId, "items"), (snap) => {
    const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(items);
  });
};

// ── ACTIVITY ─────────────────────────────────────────
export const logActivity = async (groupId, text) => {
  await addDoc(collection(db, "groups", groupId, "activity"), {
    text,
    ts: serverTimestamp(),
  });
};

export const listenToActivity = (groupId, callback) => {
  return onSnapshot(collection(db, "groups", groupId, "activity"), (snap) => {
    const activity = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.ts?.seconds || 0) - (a.ts?.seconds || 0))
      .slice(0, 20);
    callback(activity);
  });
};

// ── DEALS ────────────────────────────────────────────
export const listenToDeals = (callback) => {
  return onSnapshot(collection(db, "deals"), (snap) => {
    const deals = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    callback(deals);
  });
};

export const addDeal = async (data) => {
  await addDoc(collection(db, "deals"), {
    ...data,
    createdAt: serverTimestamp(),
  });
};

export const updateDeal = async (dealId, data) => {
  await updateDoc(doc(db, "deals", dealId), data);
};

export const deleteDeal = async (dealId) => {
  await deleteDoc(doc(db, "deals", dealId));
};